// Mirrors the mgmt-api contract-laser PUT validation (Feature #5287):
//   - is_contract_lasered and is_contract_excluded are mutually exclusive.
//   - When is_contract_lasered, all four dates + amt_contract_lasered_deductible
//     are required, and each window's start must be <= its end.
// Returns an error message, or an empty string when valid — used for inline
// validation so the dialog can block submit without an API round-trip.

export interface ContractLaserValidationInput {
  /** True when "Fully exclude this claimant from the contract" is ON. */
  isExcluded: boolean;
  incurredStart: string | null | undefined;
  incurredEnd: string | null | undefined;
  paidStart: string | null | undefined;
  paidEnd: string | null | undefined;
  /** Raw deductible input (string from the currency field) or number. */
  deductible: string | number | null | undefined;
}

const startOfDay = (value: string): Date => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Validate the contract-laser form in laser mode. When `isExcluded` is true the
 * laser inputs are not submitted, so no checks run (full exclusion needs no
 * windows/deductible). Returns "" when valid.
 */
export const validateContractLaser = ({
  isExcluded,
  incurredStart,
  incurredEnd,
  paidStart,
  paidEnd,
  deductible,
}: ContractLaserValidationInput): string => {
  // Full exclusion: laser inputs are ignored, nothing to validate.
  if (isExcluded) return "";

  if (!incurredStart || !incurredEnd || !paidStart || !paidEnd) {
    return "All four contract dates (incurred and paid windows) are required.";
  }

  const deductibleNum = typeof deductible === "number" ? deductible : parseFloat(String(deductible ?? ""));
  if (!Number.isFinite(deductibleNum) || deductibleNum <= 0) {
    return "A contract-lasered deductible greater than $0 is required.";
  }

  const incStart = startOfDay(incurredStart);
  const incEnd = startOfDay(incurredEnd);
  const pdStart = startOfDay(paidStart);
  const pdEnd = startOfDay(paidEnd);

  if ([incStart, incEnd, pdStart, pdEnd].some((d) => Number.isNaN(d.getTime()))) {
    return "One or more contract dates are invalid.";
  }

  if (incStart > incEnd) {
    return "Incurred start date must be on or before the incurred end date.";
  }
  if (pdStart > pdEnd) {
    return "Paid start date must be on or before the paid end date.";
  }

  return "";
};
